import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, FormField, Text } from "@lendcord/concord";
import { useVerifySignupMutation, type AuthTokenResponse } from "@/auth/api/auth.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { OtpBoxes } from "@/auth/components/OtpBoxes";

const OTP_VALIDITY_SECONDS = 5 * 60;
const RESEND_COOLDOWN_SECONDS = 30;

interface OtpFormProps {
  phone: string;
  email: string;
  sessionToken: string;
  // Re-runs signup with the original form values to get a fresh sessionToken
  // and a fresh pair of OTPs — there's no dedicated "resend" endpoint for the
  // signup flow, only for the (currently unused) login-OTP purpose.
  onResend: () => Promise<void>;
  onVerified: (result: AuthTokenResponse) => void;
  onEditPhone: () => void;
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function OtpForm({ phone, email, sessionToken, onResend, onVerified, onEditPhone }: OtpFormProps) {
  const intl = useIntl();
  const [verifySignup] = useVerifySignupMutation();
  const [phoneOtp, setPhoneOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [errors, setErrors] = useState<{ general?: string; phone?: string; email?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(OTP_VALIDITY_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsRemaining((seconds) => Math.max(0, seconds - 1));
      setResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const isExpired = secondsRemaining === 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (phoneOtp.length !== 6 || emailOtp.length !== 6) {
      setErrors({ general: intl.formatMessage({ id: "auth.otpForm.requiredCodeError" }) });
      return;
    }
    if (isExpired) {
      setErrors({ general: intl.formatMessage({ id: "auth.otpForm.expiredCodeError" }) });
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    try {
      const result = await verifySignup({ sessionToken, emailOtp, phoneOtp }).unwrap();
      onVerified(result);
    } catch (error) {
      if (isQueryApiError(error) && error.code === "LEND-010") {
        // "OTP not valid for TYPE: EMAIL" / "...PHONE" — the other channel's
        // already-correct code isn't lost, so only flag the wrong one.
        const incorrect = intl.formatMessage({ id: "auth.otpForm.incorrectCodeError" });
        setErrors(error.message.toUpperCase().includes("EMAIL") ? { email: incorrect } : { phone: incorrect });
      } else if (isQueryApiError(error) && error.code === "LEND-011") {
        setErrors({ general: intl.formatMessage({ id: "auth.otpForm.sessionExpiredError" }) });
      } else if (isQueryApiError(error) && error.code === "LEND-012") {
        setErrors({ general: intl.formatMessage({ id: "auth.otpForm.tooManyAttemptsError" }) });
      } else {
        setErrors({ general: intl.formatMessage({ id: "auth.otpForm.genericError" }) });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResend() {
    setIsResending(true);
    setErrors({});
    try {
      await onResend();
      setSecondsRemaining(OTP_VALIDITY_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setPhoneOtp("");
      setEmailOtp("");
    } catch (error) {
      setErrors({
        general:
          isQueryApiError(error) ? error.message : intl.formatMessage({ id: "auth.otpForm.genericError" }),
      });
    } finally {
      setIsResending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
      <Text size="sm" variant="muted">
        <FormattedMessage id="auth.otpForm.codeSentMessageBoth" values={{ phone, email }} />{" "}
        <button
          type="button"
          onClick={onEditPhone}
          className="font-medium text-accent-600 hover:underline"
        >
          <FormattedMessage id="auth.otpForm.editButton" />
        </button>
      </Text>

      {errors.general && <AlertBanner status="error">{errors.general}</AlertBanner>}

      <FormField
        label={intl.formatMessage({ id: "auth.otpForm.phoneCodeLabel" })}
        error={errors.phone}
        required
      >
        <OtpBoxes value={phoneOtp} onChange={setPhoneOtp} />
      </FormField>

      <FormField
        label={intl.formatMessage({ id: "auth.otpForm.emailCodeLabel" })}
        error={errors.email}
        required
        hint={
          errors.email
            ? undefined
            : isExpired
              ? intl.formatMessage({ id: "auth.otpForm.codeExpiredHint" })
              : intl.formatMessage(
                  { id: "auth.otpForm.validForHint" },
                  { time: formatTime(secondsRemaining) }
                )
        }
      >
        <OtpBoxes value={emailOtp} onChange={setEmailOtp} />
      </FormField>

      <Button type="submit" disabled={isSubmitting || isExpired}>
        {isSubmitting ? (
          <FormattedMessage id="auth.otpForm.submittingButton" />
        ) : (
          <FormattedMessage id="auth.otpForm.submitButton" />
        )}
      </Button>

      <Button
        type="button"
        variant="ghost"
        disabled={resendCooldown > 0 || isResending}
        onClick={handleResend}
      >
        {isResending ? (
          <FormattedMessage id="auth.otpForm.resendingButton" />
        ) : resendCooldown > 0 ? (
          <FormattedMessage id="auth.otpForm.resendInSeconds" values={{ seconds: resendCooldown }} />
        ) : (
          <FormattedMessage id="auth.otpForm.resendButton" />
        )}
      </Button>
    </form>
  );
}
